import { useState, useEffect, lazy, Suspense } from "react";
import Panel from "./Panel";
import WelcomeChecklist from "./WelcomeChecklist";
import { subscribe } from "../engine/consoleNav";

/**
 * ConsoleShell — Customer Console shell & navigation.
 *
 * Renders the operational console nav and content for a customer/tenant
 * principal. With F05/F06-A shipped the console is live end-to-end: Submit Job
 * mounts the real SubmitForm (via SubmitTab), Jobs mounts the shared JobsView
 * in its console variant (list -> detail with lifecycle, Adam/Eve reasoning,
 * outputs, logs, cancel), Billing mounts the live usage/quota screen
 * (BillingTab), and Account mounts the live profile / password / API-key screen
 * (AccountTab, F07).
 *
 * Registration is not a console screen — registration lives at the auth gate
 * (F02) since the user is already authenticated here. Monitor and Results from
 * F04 collapse into the single Jobs screen (F05 FR8).
 *
 * E2E contract: data-testid="console-view" on the root,
 * data-testid="switch-research" on the research switch, screen nav buttons by
 * visible label, LOGOUT button.
 */

/* ─── CONSOLE SCREEN DEFINITIONS ─── */
const SCREENS = [
  { id: "submit", l: "Submit Job", component: lazy(() => import("../tabs/SubmitTab.jsx")) },
  { id: "projects", l: "Projects", component: lazy(() => import("../tabs/ProjectsTab.jsx")) },
  { id: "jobs", l: "Jobs", component: lazy(() => import("../tabs/ConsoleJobsTab.jsx")) },
  { id: "billing", l: "Billing", component: lazy(() => import("../tabs/BillingTab.jsx")) },
  { id: "account", l: "Account", component: lazy(() => import("../tabs/AccountTab.jsx")) },
  { id: "guide", l: "Guide", component: lazy(() => import("../tabs/GuideTab.jsx")) },
];

const DEFAULT_SCREEN = "submit";

/**
 * OMEGA-814 — `displayName` is what the customer is CALLED (their `full_name`, falling
 * back to the username for an account created before migration 091); `user` is what they
 * SIGNED IN with, which is now an email. The badge below shows the former and falls back
 * to the latter, because a header that greets someone by their email address reads as a
 * system that never asked for their name — and this one did, at registration.
 *
 * `user` is kept as the fallback rather than removed: it is the only identity available in
 * offline-auth mode, which has no profile endpoint to read a name from.
 *
 * `access` and `onSwitchView` are gone with the operator/research dual view (OMEGA-801):
 * the Console is single-audience customer and has no view to switch to.
 */
export default function ConsoleShell({ user, displayName, onLogout }) {
  const [screen, setScreen] = useState(DEFAULT_SCREEN);
  // Bumped on every consoleNav request. It keys the active screen so the target
  // REMOUNTS and consumes its one-shot prefill even when the requested screen is
  // already active — e.g. the F12 onboarding "Submit your first job" fires while
  // Submit (the default screen) is showing, so setScreen("submit") alone would
  // be a no-op and SubmitTab's mount-only consumePrefill() would never run.
  // Plain nav-button clicks call setScreen directly and do NOT bump this, so
  // ordinary navigation keeps its screen state.
  const [navNonce, setNavNonce] = useState(0);

  // The consoleNav channel lets a zero-prop surface (the Guide, the F12
  // onboarding checklist) ask the shell to switch screens and hand the target a
  // one-shot prefill. setScreen/setNavNonce are stable setters, so subscribing
  // once on mount is enough.
  useEffect(
    () =>
      subscribe((screenId) => {
        setScreen(screenId);
        setNavNonce((n) => n + 1);
      }),
    [],
  );

  const active = SCREENS.find((s) => s.id === screen) || SCREENS[0];
  const ActiveComponent = active.component;

  return (
    <div data-testid="console-view" className="min-h-screen bg-canvas text-fg">
      {/* HEADER */}
      <div className="bg-surface border-b border-line px-6 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <div>
            <div className="font-semibold tracking-[0.18em] text-fg">
              OMEGA AI HPC <span className="text-fg3 font-normal tracking-normal text-sm ml-1">Console</span>
            </div>
          </div>
          <span className="text-xs bg-surface2 rounded-none px-2 py-1 text-fg2">{displayName || user || "customer"}</span>
        </div>

        <div className="flex items-center gap-1 flex-wrap">
          {SCREENS.map((s) => (
            <button
              key={s.id}
              onClick={() => setScreen(s.id)}
              className={`px-3 py-1.5 rounded-none text-sm transition-colors duration-150 cursor-pointer ${
                screen === s.id
                  ? "bg-accent-tint text-accent font-medium"
                  : "text-fg2 hover:text-fg"
              }`}
            >
              {s.l}
            </button>
          ))}
          <span className="w-px h-5 bg-line mx-2" />
          <button
            onClick={onLogout}
            className="px-3 py-1.5 rounded-none text-sm text-fg2 border border-line hover:text-fg transition-colors duration-150 cursor-pointer"
          >
            LOGOUT
          </button>
        </div>
      </div>

      {/* FIRST-LOGIN ONBOARDING (F12) — a value-first welcome + activation
          checklist that precedes every screen. Self-fetches its gating data and
          renders nothing for a returning/onboarded or dismissed customer. */}
      <WelcomeChecklist />

      {/* SCREEN CONTENT — live screens (Submit / Jobs / Billing / Account); any
          future screen without a component falls back to the placeholder panel. */}
      <div className="px-6 py-5">
        {ActiveComponent ? (
          <Suspense fallback={<div className="text-sm text-fg3 py-6">Loading {active.l}…</div>}>
            <ActiveComponent key={`${screen}-${navNonce}`} />
          </Suspense>
        ) : (
          <Panel title={active.l} sub={`Module ${active.module}`}>
            <div className="space-y-1.5 py-1">
              <div className="text-sm text-fg">{active.l}: coming in {active.module}.</div>
              <div className="text-xs text-fg3">Consumes: {active.consumes}</div>
              <div className="text-xs text-fg3">The screen UI ships with its own module.</div>
            </div>
          </Panel>
        )}
      </div>

      {/* FOOTER — the product name, and nothing else.

          OMEGA-826: the right-hand span read "{n} screens | tenant <uuid> | role
          <name>". A screen count is an implementation detail, and a raw tenant
          UUID and a role name are internal identifiers that mean nothing to the
          paying customer who is the Console's only audience (ADR 0004 / E06).
          The same role and tenant values come off the Account profile under
          OMEGA-833, so the two surfaces agree.

          The `access` prop and the "Research" switch that stood here are gone: the
          Console has no RBAC and one view, so there is nothing to switch to.

          The brand string is F11 FR2's, verbatim, and is not ours to reword here:
          F11 pins "Omega AI HPC Console" in two independent places (the Product
          Definition table and the Scope bullet) and FR2 names this footer as one
          of the surfaces it governs. An earlier revision of this branch read
          "Omega AI HPC Customer Console", which left the shell rendering two
          brand forms at once against the masthead above. */}
      <div className="px-6 py-3 border-t border-line text-xs text-fg3">
        <span>Omega AI HPC Console</span>
      </div>
    </div>
  );
}
